import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button/Button';
import {
  fetchAuthSessions,
  logoutAllAuthSessions,
  revokeAuthSession,
  type AuthDeviceSession,
} from '@/lib/authSession';
import { useAuth } from '@/contexts/useAuth';
import './ActiveDevicesModal.css';

function formatSeen(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  try {
    return new Date(t).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function platformLabel(platform: string): string {
  switch (platform) {
    case 'ios':
      return 'iOS';
    case 'android':
      return 'Android';
    case 'desktop':
      return 'Desktop';
    case 'web':
      return 'Web';
    default:
      return platform;
  }
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ActiveDevicesModal({ open, onClose }: Props) {
  const { logout } = useAuth();
  const [sessions, setSessions] = useState<AuthDeviceSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchAuthSessions();
      setSessions(list);
    } catch {
      setError('Не удалось загрузить устройства');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleRevoke = async (session: AuthDeviceSession) => {
    setBusyId(session.id);
    try {
      const ok = await revokeAuthSession(session.id);
      if (!ok) {
        setError('Не удалось завершить сессию');
        return;
      }
      if (session.current) {
        await logout();
        onClose();
        return;
      }
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleLogoutAll = async () => {
    setBusyId('all');
    try {
      await logoutAllAuthSessions();
      await logout();
      onClose();
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="active-devices-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="active-devices-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="active-devices-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="active-devices-modal__header">
          <h2 id="active-devices-title">Активные устройства</h2>
          <Button type="button" variant="outlined" size="s" onClick={onClose} aria-label="Закрыть">
            Закрыть
          </Button>
        </div>
        {error ? <p className="active-devices-modal__error">{error}</p> : null}
        {loading ? <p className="active-devices-modal__hint">Загрузка…</p> : null}
        {!loading && sessions.length === 0 ? (
          <p className="active-devices-modal__hint">Нет активных сессий</p>
        ) : null}
        <ul className="active-devices-modal__list">
          {sessions.map((s) => (
            <li key={s.id} className="active-devices-modal__item">
              <div className="active-devices-modal__meta">
                <div className="active-devices-modal__title-row">
                  <span className="active-devices-modal__platform">{platformLabel(s.platform)}</span>
                  {s.current ? <span className="active-devices-modal__badge">Это устройство</span> : null}
                </div>
                <div className="active-devices-modal__name">
                  {s.device_name?.trim() || s.user_agent?.slice(0, 60) || 'Устройство'}
                </div>
                <div className="active-devices-modal__seen">Активность: {formatSeen(s.last_seen_at)}</div>
              </div>
              <Button
                type="button"
                variant="outlined"
                size="s"
                disabled={busyId != null}
                onClick={() => void handleRevoke(s)}
              >
                {busyId === s.id ? '…' : s.current ? 'Выйти' : 'Завершить'}
              </Button>
            </li>
          ))}
        </ul>
        {sessions.length > 1 ? (
          <div className="active-devices-modal__footer">
            <Button
              type="button"
              variant="danger"
              size="s"
              disabled={busyId != null}
              onClick={() => void handleLogoutAll()}
            >
              {busyId === 'all' ? '…' : 'Выйти на всех устройствах'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
